/**
 * TravelDocPreface — Editor del prefacio común de Travel Documents (Traveler Info).
 *
 * Editable por Admin/Director-Operaciones/Director-Ventas.
 * El backend valida el rol; si el usuario no tiene permiso, PUT devuelve 403.
 * El prefacio se antepone al inicio de todos los Travel Documents publicados
 * como bloque colapsable "Traveler Info".
 */
import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Bold as BoldIcon, Italic as ItalicIcon, Link as LinkIcon,
  List as ListIcon, ListOrdered, Undo2, Redo2, Heading2, Heading3,
  Save, RotateCcw, Loader2, Info, Eye, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/* ─────────────────────── Toolbar del editor ─────────────────────── */

const ToolbarButton = ({ onClick, active, disabled, title, children, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
    data-testid={testid}
    className={`inline-flex h-8 w-8 items-center justify-center rounded transition ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
    {children}
  </button>
);

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;
  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL:", prev);
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  return (
    <div className="flex flex-wrap items-center gap-1 border-b bg-muted/30 p-1.5" data-testid="td-preface-toolbar">
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Título grande" testid="tb-h2"><Heading2 className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subtítulo" testid="tb-h3"><Heading3 className="h-4 w-4" /></ToolbarButton>
      <span className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita (Ctrl+B)" testid="tb-bold"><BoldIcon className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva (Ctrl+I)" testid="tb-italic"><ItalicIcon className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton onClick={promptLink} active={editor.isActive("link")} title="Enlace" testid="tb-link"><LinkIcon className="h-4 w-4" /></ToolbarButton>
      <span className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista con viñetas" testid="tb-ul"><ListIcon className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada" testid="tb-ol"><ListOrdered className="h-4 w-4" /></ToolbarButton>
      <span className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer" testid="tb-undo"><Undo2 className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer" testid="tb-redo"><Redo2 className="h-4 w-4" /></ToolbarButton>
    </div>
  );
};

/* ─────────────────────── Vista previa ─────────────────────── */

const PrefacePreview = ({ html }) => (
  <div className="prose prose-sm max-w-none rounded-md border bg-muted/10 p-4"
    style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
    data-testid="td-preface-preview"
    dangerouslySetInnerHTML={{ __html: html || "<p class='italic text-muted-foreground'>Sin contenido</p>" }} />
);

/* ─────────────────────── Página principal ─────────────────────── */

export default function TravelDocPreface() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["td-preface"],
    queryFn: async () => (await api.get("/td-preface")).data,
  });

  const [html, setHtml] = useState("");
  const [dirty, setDirty] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, blockquote: false, horizontalRule: false }),
      Link.configure({
        openOnClick: false, autolink: true,
        HTMLAttributes: { class: "text-primary underline", target: "_blank", rel: "noopener noreferrer" },
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[500px] px-4 py-3",
        "data-testid": "td-preface-editor-content",
      },
    },
    onUpdate: ({ editor }) => {
      const next = editor.getHTML();
      setHtml(next);
      setDirty(true);
    },
  });

  // Cargar contenido inicial cuando el query resuelve
  useEffect(() => {
    if (!editor || !data?.html) return;
    if (!html) {
      editor.commands.setContent(data.html, false);
      setHtml(data.html);
      setDirty(false);
    }
  }, [editor, data?.html, html]);

  const saveMutation = useMutation({
    mutationFn: async () => (await api.put("/td-preface", { html })).data,
    onSuccess: () => {
      toast.success("Prefacio guardado. Se aplicará a todos los Travel Documents.");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["td-preface"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "No se pudo guardar el prefacio"),
  });

  const resetMutation = useMutation({
    mutationFn: async () => (await api.post("/td-preface/reset")).data,
    onSuccess: async () => {
      toast.success("Prefacio restaurado al texto predeterminado");
      const fresh = (await api.get("/td-preface")).data;
      editor?.commands.setContent(fresh.html, false);
      setHtml(fresh.html);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["td-preface"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "No se pudo restaurar"),
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center" data-testid="td-preface-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6" data-testid="td-preface-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold" data-testid="td-preface-title">Prefacio de Travel Documents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Este contenido se muestra como bloque colapsable <strong>«Traveler Info»</strong> al inicio de todos los Travel Documents publicados.
          </p>
          {data?.updated_at && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="td-preface-meta">
              Última edición: {new Date(data.updated_at).toLocaleString("es-ES")}
              {data.updated_by ? ` · ${data.updated_by}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="td-preface-reset-btn">
                <RotateCcw className="mr-1.5 h-4 w-4" />Restaurar predeterminado
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="td-preface-reset-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Restaurar contenido por defecto?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se sobrescribirá el prefacio actual con el texto predeterminado (Traveler Info original). Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="td-preface-reset-cancel">Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => resetMutation.mutate()} data-testid="td-preface-reset-confirm">Restaurar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={() => saveMutation.mutate()} disabled={!dirty || saveMutation.isPending} data-testid="td-preface-save-btn">
            {saveMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Guardar cambios
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          <strong>Solo Admin, Director-Operaciones y Director-Ventas</strong> pueden guardar cambios. Los enlaces (por ejemplo <em>Full Terms &amp; Conditions</em>) se mantienen al guardar y son navegables desde el visor público.
        </span>
      </div>

      <Card>
        <Tabs defaultValue="editor" className="w-full">
          <CardHeader className="border-b pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Contenido del prefacio</CardTitle>
              <TabsList data-testid="td-preface-tabs">
                <TabsTrigger value="editor" data-testid="td-preface-tab-editor"><Pencil className="mr-1.5 h-3.5 w-3.5" />Editor</TabsTrigger>
                <TabsTrigger value="preview" data-testid="td-preface-tab-preview"><Eye className="mr-1.5 h-3.5 w-3.5" />Vista previa</TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <TabsContent value="editor" className="m-0">
              <EditorToolbar editor={editor} />
              <EditorContent editor={editor} />
            </TabsContent>
            <TabsContent value="preview" className="m-0 p-4">
              <PrefacePreview html={html} />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
}
